import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { U3140_PDF_ANNOTATION_FILL_NONE } from "./U3140PdfAnnotationsPalette";

/** Unité logique pour les URLs document (`pdf-file`, `annotations`). Les préréglages utilisent toujours `U3142`. */
export type U3140DocumentLogicalUnit = "U3141" | "U3142";

/** Variante `GET …/pdf-file` : aplat avec annotations (défaut API) ou original sans aplat (éditeur). */
export type U3140DocumentPdfFileVariant = "annotated" | "original";

function u3140DocumentEndpoints(lu: U3140DocumentLogicalUnit) {
  const docBase = (documentId: string | number) => `${lu}/document/${documentId}`;
  return {
    GETDOCUMENTPDFFILE: (documentId: string | number, variant?: U3140DocumentPdfFileVariant) => {
      const base = `${docBase(documentId)}/pdf-file`;
      if (variant === undefined) {
        return base;
      }
      return `${base}?variant=${variant}`;
    },
    GETDOCUMENTANNOTATIONS: (documentId: string | number) => `${docBase(documentId)}/annotations`,
    PUTDOCUMENTANNOTATIONS: (documentId: string | number) => `${docBase(documentId)}/annotations`,
  };
}

const U3140_PRESET_LOGICAL_UNIT = "U3142" as const;

const u3140PresetEndpoints = {
  GETPDFANNOTATIONTOOLBARPRESETS: `${U3140_PRESET_LOGICAL_UNIT}/pdf-annotation-toolbar-presets`,
  CREATEPDFANNOTATIONTOOLBARPRESET: `${U3140_PRESET_LOGICAL_UNIT}/pdf-annotation-toolbar-presets`,
  UPDATEPDFANNOTATIONTOOLBARPRESET: (presetId: string | number) =>
    `${U3140_PRESET_LOGICAL_UNIT}/pdf-annotation-toolbar-presets/${presetId}`,
  DELETEPDFANNOTATIONTOOLBARPRESET: (presetId: string | number) =>
    `${U3140_PRESET_LOGICAL_UNIT}/pdf-annotation-toolbar-presets/${presetId}`,
};
/** Annotation PDF (repère bas-gauche de la page, coordonnées 0–1). */
/** Alignement horizontal du bloc texte (Fabric `Textbox` / `IText.textAlign`). */
export type U3140PdfAnnotationTextAlign = "left" | "center" | "right";

/** Alignement vertical du texte **dans** le cadre (hauteur > contenu) ; optionnel côté API. */
export type U3140PdfAnnotationTextBoxVerticalAlign = "top" | "middle" | "bottom";

/** Interprétation de `y` pour le texte (contrat API U3142). */
export type U3140PdfAnnotationTextVerticalAnchor = "baseline" | "textTop";

export interface U3140PdfAnnotationMetadata {
  annotationId?: string;
  createdBy?: string;
  createdByUser?: string;
  createdAt?: string;
}

export type U3140PdfAnnotationShape =
  | ({
      type: "rect";
      pageIndex: number;
      zIndex?: number;
      x: number;
      y: number;
      w: number;
      h: number;
      fillColor?: string;
      opacity?: number;
      strokeColor?: string;
      strokeWidth?: number;
      /** Largeur du viewport canvas pdf.js (px) au moment de l’enregistrement — pour convertir l’épaisseur en points PDF. */
      layoutPageWidthPx?: number;
    } & U3140PdfAnnotationMetadata)
  | ({
      type: "line";
      pageIndex: number;
      zIndex?: number;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      strokeColor?: string;
      strokeWidth?: number;
      opacity?: number;
      /** Même rôle que pour le texte : `strokeWidth` est en px canvas ; le PDF utilise `pw * strokeWidth / layoutPageWidthPx`. */
      layoutPageWidthPx?: number;
    } & U3140PdfAnnotationMetadata)
  | ({
      type: "arrow";
      pageIndex: number;
      zIndex?: number;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      strokeColor?: string;
      strokeWidth?: number;
      opacity?: number;
      /** Même rôle : épaisseur tige / polygone en px canvas ; conversion à l’aplatissement. */
      layoutPageWidthPx?: number;
    } & U3140PdfAnnotationMetadata)
  | ({
      type: "text";
      pageIndex: number;
      zIndex?: number;
      x: number;
      /**
       * 0–1 bas-gauche page : **bord haut** du cadre si `textVerticalAnchor: "textTop"` ou format
       * `fontSizePx` + `layoutPageWidthPx` ; sinon (rétro `fontSizePts` seul) **bas** du cadre.
       */
      y: number;
      text: string;
      /** Taille CSS éditeur (px) ; avec `layoutPageWidthPx`, ne pas envoyer `fontSizePts`. */
      fontSizePx?: number;
      /** Largeur canvas pdf.js (px CSS) ; avec `fontSizePx` pour conversion côté serveur. */
      layoutPageWidthPx?: number;
      /** Rétro : points PDF si pas de couple px + layout. */
      fontSizePts?: number;
      /** Couleur des glyphes (équivalent `fill` Fabric). */
      color?: string;
      /** Fond derrière le bloc texte (`backgroundColor` Fabric). */
      backgroundColor?: string;
      strokeColor?: string;
      strokeWidth?: number;
      opacity?: number;
      /** Alignement du texte dans le bloc (`left` si absent pour rétrocompatibilité). */
      textAlign?: U3140PdfAnnotationTextAlign;
      /**
       * Largeur du bloc texte (retour à la ligne), fraction de la largeur viewport PDF (0–1).
       * Absent pour les anciennes annotations `IText` : le client applique une largeur par défaut.
       */
      textBoxWidth?: number;
      /**
       * Hauteur du cadre texte, fraction de la **hauteur** du viewport PDF (0–1).
       * Absent = hauteur auto (contenu). Persisté par l’éditeur pour redimensionnement vertical.
       */
      textBoxHeight?: number;
      /** Alignement vertical du texte dans le cadre (`top` si absent). */
      textBoxVerticalAlign?: U3140PdfAnnotationTextBoxVerticalAlign;
      /** Interprétation de `y` (API U3142). */
      textVerticalAnchor?: U3140PdfAnnotationTextVerticalAnchor;
      /** Interligne points PDF (optionnel). */
      lineHeightPts?: number;
    } & U3140PdfAnnotationMetadata);

/**
 * Bloc d’annotations pour **un lot** de la chaîne (segment du PDF fusionné).
 * Persistance serveur : une ligne dans `rf_lot_detail_pdf_annotation` par `lotDetailId`.
 */
export interface U3140DocumentPdfAnnotationsLotPayload {
  /** Identifiant du lot de la chaîne (`rf_lot_detail`). Clé de persistance côté serveur. */
  lotDetailId: string;
  schemaVersion: 1;
  shapes: U3140PdfAnnotationShape[];
  /**
   * **PUT uniquement** — absent sur GET. `true` si l’utilisateur a eu au moins une action d’ajout,
   * modification ou suppression sur ce segment depuis le dernier GET ou la dernière sauvegarde réussie,
   * et que le contenu diffère encore de cette baseline (indicateur graphe `indexMostRecent` / U3141).
   * Ne pas envoyer `true` pour tous les segments par défaut.
   */
  annotated?: boolean;
}

/**
 * Corps **PUT/POST** `/U3142/document/:documentId/annotations` (rupture de contrat) :
 * agrégat par lot. L’ordre et les `lotDetailId` de `lotPayloads` doivent correspondre exactement
 * à la réponse `GET` (sinon `BU314212 — AnnotationChainPayloadMismatch`).
 */
export interface U3140DocumentPdfAnnotationsPayload {
  schemaVersion: 1;
  lotPayloads: U3140DocumentPdfAnnotationsLotPayload[];
}

/**
 * Segment d’un PDF fusionné (ex. chaîne annotée) : plage de pages + libellé pour navigation rapide.
 * Fourni par le backend sur GET …/annotations (optionnel). Non renvoyé au PUT.
 * Ordre aligné 1:1 avec `lotPayloads` (même ordre de fusion).
 */
export interface U3140DocumentPageSegment {
  /** Libellé affiché (ex. lot + no projet ou nom de fichier source). */
  label: string;
  /** Première page du segment dans le PDF fusionné (1-based, inclusive). */
  startPage: number;
  /** Dernière page du segment (1-based, inclusive). */
  endPage: number;
  /** Identifiant document source si connu côté fusion. */
  sourceDocumentId?: string;
  projectNumber?: string;
}

/** Réponse GET annotations (agrégat par lot + segments de pagination optionnels). */
export interface U3140DocumentPdfAnnotationsGetResult extends U3140DocumentPdfAnnotationsPayload {
  pageSegments?: U3140DocumentPageSegment[];
}

export type U3140PdfAnnotationPresetShapeKind = "rect" | "line" | "arrow" | "text";

export interface U3140PdfAnnotationToolbarStylePreset {
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
  textColor: string;
  fontSizePts: number;
  textAlign: U3140PdfAnnotationTextAlign;
  textBoxWidthNorm: number;
  textBoxVerticalAlign: U3140PdfAnnotationTextBoxVerticalAlign;
  /** Rectangle : largeur initiale en px canvas (préféré). */
  defaultWidthPx?: number;
  /** Rectangle : hauteur initiale en px canvas (préféré). */
  defaultHeightPx?: number;
  /** @deprecated Rétrocompat — préférer defaultWidthPx. */
  defaultWidthNorm?: number;
  /** @deprecated Rétrocompat — préférer defaultHeightPx. */
  defaultHeightNorm?: number;
}

/** Valeurs par défaut / legacy (px canvas). */
export const U3140_RECT_DEFAULT_WIDTH_PX = 180;
export const U3140_RECT_DEFAULT_HEIGHT_PX = 100;

/** Plafonds saisie formulaire préréglage (px). */
export const U3140_RECT_PRESET_PX_MIN = 8;
export const U3140_RECT_PRESET_PX_MAX = 4000;

/** Référence affichage migration norm → px (ancien % viewport). */
const U3140_RECT_NORM_REFERENCE_VW = 720;
const U3140_RECT_NORM_REFERENCE_VH = 833;

export function u3140ClampRectPx(n: number, maxPx = U3140_RECT_PRESET_PX_MAX): number {
  if (!Number.isFinite(n)) {
    return U3140_RECT_DEFAULT_WIDTH_PX;
  }
  return Math.min(maxPx, Math.max(U3140_RECT_PRESET_PX_MIN, Math.round(n)));
}

function u3140HasRectDefaultSizePx(sty: {
  defaultWidthPx?: number;
  defaultHeightPx?: number;
}): boolean {
  return Number.isFinite(sty.defaultWidthPx) && Number.isFinite(sty.defaultHeightPx);
}

function u3140HasRectDefaultSizeNorm(sty: {
  defaultWidthNorm?: number;
  defaultHeightNorm?: number;
}): boolean {
  return Number.isFinite(sty.defaultWidthNorm) && Number.isFinite(sty.defaultHeightNorm);
}

/** Taille canvas (px) à la pose d’un rectangle : px préréglage, normes legacy, ou défaut produit. */
export function u3140ResolveRectCanvasSize(
  vw: number,
  vh: number,
  sty: Pick<
    U3140PdfAnnotationToolbarStylePreset,
    "defaultWidthPx" | "defaultHeightPx" | "defaultWidthNorm" | "defaultHeightNorm"
  >
): { w: number; h: number } {
  if (u3140HasRectDefaultSizePx(sty)) {
    const w = u3140ClampRectPx(sty.defaultWidthPx as number, vw);
    const h = u3140ClampRectPx(sty.defaultHeightPx as number, vh);
    return {
      w: Math.max(1, Math.min(w, vw)),
      h: Math.max(1, Math.min(h, vh)),
    };
  }
  if (u3140HasRectDefaultSizeNorm(sty)) {
    const w = vw * Math.min(1, Math.max(0.04, sty.defaultWidthNorm as number));
    const h = vh * Math.min(1, Math.max(0.04, sty.defaultHeightNorm as number));
    return { w: Math.max(1, w), h: Math.max(1, h) };
  }
  return {
    w: Math.min(U3140_RECT_DEFAULT_WIDTH_PX, vw),
    h: Math.min(U3140_RECT_DEFAULT_HEIGHT_PX, vh),
  };
}

/** Styles rect initiaux pour un nouveau préréglage (taille par défaut incluse). */
export function u3140NewPresetRectStyleSeed(): Partial<U3140PdfAnnotationToolbarStylePreset> {
  return {
    defaultWidthPx: U3140_RECT_DEFAULT_WIDTH_PX,
    defaultHeightPx: U3140_RECT_DEFAULT_HEIGHT_PX,
  };
}

/** Valeur px affichée dans le formulaire (px persistés ou conversion depuis norm). */
export function u3140RectDisplayWidthPx(rect: Pick<
  U3140PdfAnnotationToolbarStylePreset,
  "defaultWidthPx" | "defaultWidthNorm"
>): number {
  if (Number.isFinite(rect.defaultWidthPx)) {
    return rect.defaultWidthPx as number;
  }
  if (Number.isFinite(rect.defaultWidthNorm)) {
    return Math.round((rect.defaultWidthNorm as number) * U3140_RECT_NORM_REFERENCE_VW);
  }
  return U3140_RECT_DEFAULT_WIDTH_PX;
}

export function u3140RectDisplayHeightPx(rect: Pick<
  U3140PdfAnnotationToolbarStylePreset,
  "defaultHeightPx" | "defaultHeightNorm"
>): number {
  if (Number.isFinite(rect.defaultHeightPx)) {
    return rect.defaultHeightPx as number;
  }
  if (Number.isFinite(rect.defaultHeightNorm)) {
    return Math.round((rect.defaultHeightNorm as number) * U3140_RECT_NORM_REFERENCE_VH);
  }
  return U3140_RECT_DEFAULT_HEIGHT_PX;
}

/**
 * Avant POST/PUT préréglage : persiste les px affichés (supprime les normes obsolètes).
 */
export function u3140MaterializeRectPresetStylesForSave(
  styles: U3140PdfAnnotationToolbarPresetStyles
): U3140PdfAnnotationToolbarPresetStyles {
  const rect = styles.rect;
  const {
    defaultWidthNorm: _wn,
    defaultHeightNorm: _hn,
    ...rectBase
  } = rect;
  return {
    ...styles,
    rect: {
      ...rectBase,
      defaultWidthPx: u3140ClampRectPx(u3140RectDisplayWidthPx(rect)),
      defaultHeightPx: u3140ClampRectPx(u3140RectDisplayHeightPx(rect)),
    },
  };
}

/** Source taille rect à la pose : préréglage appliqué (prioritaire) puis défauts barre d’outils. */
export function u3140RectStyleForPlacement(
  appliedPresetId: string | null,
  presets: readonly U3140PdfAnnotationToolbarPreset[],
  defaultsRect: Pick<
    U3140PdfAnnotationToolbarStylePreset,
    "defaultWidthPx" | "defaultHeightPx" | "defaultWidthNorm" | "defaultHeightNorm"
  >
): Pick<
  U3140PdfAnnotationToolbarStylePreset,
  "defaultWidthPx" | "defaultHeightPx" | "defaultWidthNorm" | "defaultHeightNorm"
> {
  if (appliedPresetId) {
    const preset = presets.find((p) => String(p.id) === appliedPresetId);
    if (preset?.styles?.rect) {
      const merged = u3140MergeToolbarPresetStyles(preset.styles);
      return merged.rect;
    }
  }
  return defaultsRect;
}

export type U3140PdfAnnotationToolbarPresetStyles = Record<
  U3140PdfAnnotationPresetShapeKind,
  U3140PdfAnnotationToolbarStylePreset
>;

const U3140_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM = 0.38;

/**
 * Bloc de style par type (aligné sur l’éditeur `u3140InitialDefaultsByKind`) : utilisé pour
 * compléter un JSON API partiel (ex. préréglages sans `line` / `arrow`).
 */
function u3140DefaultToolbarStylePresetForKind(kind: U3140PdfAnnotationPresetShapeKind): U3140PdfAnnotationToolbarStylePreset {
  const base: U3140PdfAnnotationToolbarStylePreset = {
    fillColor: "#FFCC00",
    strokeColor: "#000000",
    strokeWidth: 2,
    opacity: 0.55,
    textColor: "#111827",
    fontSizePts: 14,
    textAlign: "left",
    textBoxWidthNorm: U3140_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM,
    textBoxVerticalAlign: "top",
  };
  if (kind === "rect") {
    return { ...base, fillColor: "#2563EB", opacity: 1 };
  }
  if (kind === "line" || kind === "arrow") {
    return { ...base, fillColor: U3140_PDF_ANNOTATION_FILL_NONE };
  }
  if (kind === "text") {
    return {
      ...base,
      fillColor: U3140_PDF_ANNOTATION_FILL_NONE,
      textColor: "#000000",
      strokeWidth: 0,
      fontSizePts: 24,
      opacity: 1,
    };
  }
  return base;
}

function u3140MergeOneToolbarStylePreset(
  def: U3140PdfAnnotationToolbarStylePreset,
  partial: U3140PdfAnnotationToolbarStylePreset | null | undefined
): U3140PdfAnnotationToolbarStylePreset {
  if (!partial) {
    return { ...def };
  }
  const o: U3140PdfAnnotationToolbarStylePreset = { ...def };
  (Object.keys(partial) as (keyof U3140PdfAnnotationToolbarStylePreset)[]).forEach((key) => {
    const v = partial[key];
    if (v !== undefined) {
      (o as unknown as Record<string, unknown>)[key as string] = v as unknown;
    }
  });
  return o;
}

/** Complète chaque clé `rect` | `line` | `arrow` | `text` avec les défauts (GET partiel, ancien backend). */
export function u3140MergeToolbarPresetStyles(
  input: Partial<U3140PdfAnnotationToolbarPresetStyles> | null | undefined
): U3140PdfAnnotationToolbarPresetStyles {
  const kinds: U3140PdfAnnotationPresetShapeKind[] = ["rect", "line", "arrow", "text"];
  const out = {} as U3140PdfAnnotationToolbarPresetStyles;
  for (const k of kinds) {
    out[k] = u3140MergeOneToolbarStylePreset(u3140DefaultToolbarStylePresetForKind(k), input?.[k]);
  }
  return out;
}

const U3140_TOOLBAR_PRESET_SIDEBAR_FALLBACK_HEX = [
  "#FDE047",
  "#BEF264",
  "#FCA5A5",
  "#93C5FD",
  "#FDBA74",
  "#DDD6FE",
  "#FECACA",
  "#A7F3D0",
] as const;

function u3140StableHashFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function u3140IsUsableFillHexForSidebar(color: string | undefined | null): color is string {
  if (color == null || typeof color !== "string") {
    return false;
  }
  const s = color.trim();
  if (s === "" || s === U3140_PDF_ANNOTATION_FILL_NONE || s.toLowerCase() === "transparent") {
    return false;
  }
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s);
}

function u3140RgbFromHexForSidebar(hex: string): { r: number; g: number; b: number } {
  let h = hex.trim();
  if (!h.startsWith("#")) {
    h = `#${h}`;
  }
  if (h.length === 4) {
    const r = h[1];
    const g = h[2];
    const b = h[3];
    h = `#${r}${r}${g}${g}${b}${b}`;
  }
  const body = h.slice(1, 7);
  const m = /^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(body);
  if (!m) {
    return { r: 0.92, g: 0.92, b: 0.92 };
  }
  return {
    r: parseInt(m[1], 16) / 255,
    g: parseInt(m[2], 16) / 255,
    b: parseInt(m[3], 16) / 255,
  };
}

function u3140ChannelToLinear(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Luminance relative WCAG (0–1) pour un hex #RGB / #RRGGBB / #RRGGBBAA (seuls les 6 premiers comptent). */
export function u3140RelativeLuminanceForHex(hex: string): number {
  const { r, g, b } = u3140RgbFromHexForSidebar(hex);
  const R = u3140ChannelToLinear(r);
  const G = u3140ChannelToLinear(g);
  const B = u3140ChannelToLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/** Couleur de texte lisible sur un fond hex. */
export function u3140ContrastingForegroundHexForBackground(hex: string): "#000000" | "#ffffff" {
  return u3140RelativeLuminanceForHex(hex) > 0.55 ? "#000000" : "#ffffff";
}

/** Fond de la barre latérale préréglages (Tailwind `gray-50`) pour fusion contraste / opacité. */
const U3140_TOOLBAR_PRESET_SIDEBAR_NAV_BG = { r: 249 / 255, g: 250 / 255, b: 251 / 255 };

function u3140Clamp01(n: number): number {
  if (!Number.isFinite(n)) {
    return 1;
  }
  return Math.min(1, Math.max(0, n));
}

function u3140HexFromRgb255(r255: number, g255: number, b255: number): string {
  const h = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n)))
      .toString(16)
      .padStart(2, "0");
  return `#${h(r255)}${h(g255)}${h(b255)}`;
}

/** Couleur perçue du remplissage sur le fond gris de la sidebar (pour contraste du libellé). */
function u3140BlendFillOnSidebarNav(hex: string, alpha: number): string {
  const { r, g, b } = u3140RgbFromHexForSidebar(hex);
  const a = u3140Clamp01(alpha);
  const { r: br, g: bg, b: bb } = U3140_TOOLBAR_PRESET_SIDEBAR_NAV_BG;
  const rr = br * (1 - a) + r * a;
  const gg = bg * (1 - a) + g * a;
  const bb2 = bb * (1 - a) + b * a;
  return u3140HexFromRgb255(rr * 255, gg * 255, bb2 * 255);
}

function u3140NormalizeSidebarFillHex(rawIn: string): string {
  const raw = rawIn.trim();
  if (raw.length >= 7) {
    return `#${raw.slice(1, 7).toLowerCase()}`;
  }
  return raw;
}

type U3140ToolbarPresetSidebarFillSource = { hex: string; opacity: number };

function u3140ToolbarPresetSidebarFillSource(
  styles: U3140PdfAnnotationToolbarPresetStyles | null | undefined,
  presetId: string
): U3140ToolbarPresetSidebarFillSource {
  const merged = u3140MergeToolbarPresetStyles(styles);
  if (u3140IsUsableFillHexForSidebar(merged.rect.fillColor)) {
    return {
      hex: u3140NormalizeSidebarFillHex(merged.rect.fillColor),
      opacity: u3140Clamp01(merged.rect.opacity),
    };
  }
  if (u3140IsUsableFillHexForSidebar(merged.text.fillColor)) {
    return {
      hex: u3140NormalizeSidebarFillHex(merged.text.fillColor),
      opacity: u3140Clamp01(merged.text.opacity),
    };
  }
  const idx = u3140StableHashFromId(String(presetId)) % U3140_TOOLBAR_PRESET_SIDEBAR_FALLBACK_HEX.length;
  return { hex: U3140_TOOLBAR_PRESET_SIDEBAR_FALLBACK_HEX[idx], opacity: 1 };
}

/**
 * Fond + texte pour une ligne de préréglage : `backgroundColor` en rgba avec l’opacité du style source
 * (rectangle ou texte), couleur de libellé contrastée sur le fond perçu (fusion avec le gris de la barre).
 */
export function u3140ToolbarPresetSidebarRowAppearance(
  styles: U3140PdfAnnotationToolbarPresetStyles | null | undefined,
  presetId: string
): { backgroundColor: string; color: "#000000" | "#ffffff" } {
  const { hex, opacity } = u3140ToolbarPresetSidebarFillSource(styles, presetId);
  const { r, g, b } = u3140RgbFromHexForSidebar(hex);
  const blendedHex = u3140BlendFillOnSidebarNav(hex, opacity);
  return {
    backgroundColor: `rgba(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)},${opacity})`,
    color: u3140ContrastingForegroundHexForBackground(blendedHex),
  };
}

/**
 * Couleur de fond opaque pour une ligne de liste de préréglages (hex seul, sans opacité).
 * @see u3140ToolbarPresetSidebarRowAppearance pour l’affichage avec opacité.
 */
export function u3140ToolbarPresetSidebarBackgroundHex(
  styles: U3140PdfAnnotationToolbarPresetStyles | null | undefined,
  presetId: string
): string {
  return u3140ToolbarPresetSidebarFillSource(styles, presetId).hex;
}

export interface U3140PdfAnnotationToolbarPreset {
  id: string;
  label: string;
  /** Texte d’infobulle au survol du préréglage dans la liste latérale (si renseigné). */
  hint?: string;
  sortOrder?: number;
  styles: U3140PdfAnnotationToolbarPresetStyles;
  createdAt?: string;
  updatedAt?: string;
}

export interface U3140PdfAnnotationToolbarPresetCreatePayload {
  label: string;
  styles: U3140PdfAnnotationToolbarPresetStyles;
  hint?: string;
}

export interface U3140PdfAnnotationToolbarPresetUpdatePayload {
  label?: string;
  styles?: U3140PdfAnnotationToolbarPresetStyles;
  hint?: string;
  sortOrder?: number;
}

/** Infobulle liste latérale : `hint` du préréglage si non vide, sinon titre de repli (ex. « Appliquer … »). */
export function u3140ToolbarPresetSidebarHoverTitle(
  preset: U3140PdfAnnotationToolbarPreset,
  fallbackTitle: string
): string {
  const h = preset.hint?.trim();
  return h ? h : fallbackTitle;
}

function u3140UnwrapResult<T>(data: unknown): T {
  const d = data as { result?: T };
  return (d?.result ?? data) as T;
}

function u3140ParsePageSegments(raw: unknown): U3140DocumentPageSegment[] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const arr = (raw as { pageSegments?: unknown }).pageSegments;
  if (!Array.isArray(arr) || arr.length === 0) return undefined;
  const out: U3140DocumentPageSegment[] = [];
  for (const it of arr) {
    if (!it || typeof it !== "object") continue;
    const o = it as Record<string, unknown>;
    const label = typeof o.label === "string" ? o.label.trim() : "";
    const startPage = Number(o.startPage);
    const endPage = Number(o.endPage);
    if (!label || !Number.isFinite(startPage) || !Number.isFinite(endPage)) continue;
    const sp = Math.floor(startPage);
    const ep = Math.floor(endPage);
    if (sp < 1 || ep < sp) continue;
    out.push({
      label,
      startPage: sp,
      endPage: ep,
      sourceDocumentId: typeof o.sourceDocumentId === "string" ? o.sourceDocumentId : undefined,
      projectNumber: typeof o.projectNumber === "string" ? o.projectNumber : undefined,
    });
  }
  return out.length ? out : undefined;
}

function u3140ParseLotPayloads(raw: unknown): U3140DocumentPdfAnnotationsLotPayload[] {
  if (!raw || typeof raw !== "object") return [];
  const arr = (raw as { lotPayloads?: unknown }).lotPayloads;
  if (!Array.isArray(arr)) return [];
  const out: U3140DocumentPdfAnnotationsLotPayload[] = [];
  for (const it of arr) {
    if (!it || typeof it !== "object") continue;
    const o = it as Record<string, unknown>;
    const lotDetailId = typeof o.lotDetailId === "string" ? o.lotDetailId : "";
    if (!lotDetailId) continue;
    const shapes = Array.isArray(o.shapes) ? (o.shapes as U3140PdfAnnotationShape[]) : [];
    out.push({ lotDetailId, schemaVersion: 1, shapes });
  }
  return out;
}

/**
 * GET fichier document (PDF binaire via API).
 * @param logicalUnit — `U3141` ou `U3142` selon l’origine du document.
 */
export function doGetDocumentPdfFileArrayBuffer(
  axios: AxiosInstance,
  documentId: string | number,
  variant: U3140DocumentPdfFileVariant | undefined,
  logicalUnit: U3140DocumentLogicalUnit
): Promise<ArrayBuffer> {
  const ep = u3140DocumentEndpoints(logicalUnit);
  return axios
    .get(ep.GETDOCUMENTPDFFILE(documentId, variant), { responseType: "arraybuffer" })
    .then((res) => res.data as ArrayBuffer);
}

/** GET annotations JSON (agrégat par lot). Peut inclure `pageSegments` si le backend les fournit. */
export function doGetDocumentPdfAnnotations(
  axios: AxiosInstance,
  documentId: string | number,
  logicalUnit: U3140DocumentLogicalUnit
): Promise<U3140DocumentPdfAnnotationsGetResult> {
  const ep = u3140DocumentEndpoints(logicalUnit);
  return axios.get(ep.GETDOCUMENTANNOTATIONS(documentId)).then((res) => {
    const raw = u3140UnwrapResult<unknown>(res.data);
    return {
      schemaVersion: 1,
      lotPayloads: u3140ParseLotPayloads(raw),
      pageSegments: u3140ParsePageSegments(raw),
    };
  });
}

/**
 * PUT annotations JSON + matérialisation PDF côté serveur.
 * Le corps doit respecter l’ordre et les `lotDetailId` renvoyés par le GET (sinon `BU314212`).
 */
export function doPutDocumentPdfAnnotations(
  axios: AxiosInstance,
  documentId: string | number,
  payload: U3140DocumentPdfAnnotationsPayload,
  logicalUnit: U3140DocumentLogicalUnit
) {
  const ep = u3140DocumentEndpoints(logicalUnit);
  return axios.put(ep.PUTDOCUMENTANNOTATIONS(documentId), payload);
}

function u3140CoerceOptionalPx(v: unknown): number | undefined {
  if (v === undefined || v === null) {
    return undefined;
  }
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? Math.round(n) : undefined;
}

/** Réinjecte `defaultWidthPx` / `defaultHeightPx` depuis le JSON API brut (après merge). */
export function u3140PreserveRectSizeFromRawApiStyles(
  raw: unknown,
  merged: U3140PdfAnnotationToolbarPresetStyles
): U3140PdfAnnotationToolbarPresetStyles {
  if (!raw || typeof raw !== "object") {
    return merged;
  }
  const rectRaw = (raw as Record<string, unknown>).rect;
  if (!rectRaw || typeof rectRaw !== "object") {
    return merged;
  }
  const rr = rectRaw as Record<string, unknown>;
  const w = u3140CoerceOptionalPx(rr.defaultWidthPx);
  const h = u3140CoerceOptionalPx(rr.defaultHeightPx);
  if (w === undefined && h === undefined) {
    return merged;
  }
  return {
    ...merged,
    rect: {
      ...merged.rect,
      ...(w !== undefined ? { defaultWidthPx: u3140ClampRectPx(w) } : {}),
      ...(h !== undefined ? { defaultHeightPx: u3140ClampRectPx(h) } : {}),
    },
  };
}

function u3140ParsePdfAnnotationToolbarPresets(rawData: unknown): U3140PdfAnnotationToolbarPreset[] {
  /** Tableau direct, ou enveloppe `U2000ResultsResponseDto` avec clés `result`, `results` ou `data`. */
  const raw =
    Array.isArray(rawData) ? rawData : (rawData as { result?: unknown[]; results?: unknown[]; data?: unknown[] } | null);
  const arr = Array.isArray(raw) ? raw : raw?.result ?? raw?.results ?? raw?.data ?? [];
  if (!Array.isArray(arr)) {
    return [];
  }
  const out: U3140PdfAnnotationToolbarPreset[] = [];
  for (const it of arr) {
    if (!it || typeof it !== "object") continue;
    const o = it as Record<string, unknown>;
    const id = typeof o.id === "string" || typeof o.id === "number" ? String(o.id) : "";
    const label = typeof o.label === "string" ? o.label.trim() : "";
    const styles = o.styles;
    if (!id || !label || !styles || typeof styles !== "object") continue;
    const hintRaw = o.hint;
    let hint: string | undefined;
    if (typeof hintRaw === "string") {
      const t = hintRaw.trim();
      hint = t !== "" ? t : undefined;
    } else if (hintRaw != null && (typeof hintRaw === "number" || typeof hintRaw === "boolean")) {
      hint = String(hintRaw);
    }
    out.push({
      id,
      label,
      ...(hint !== undefined ? { hint } : {}),
      sortOrder: Number.isFinite(Number(o.sortOrder)) ? Number(o.sortOrder) : undefined,
      styles: u3140PreserveRectSizeFromRawApiStyles(
        styles,
        u3140MergeToolbarPresetStyles(styles as U3140PdfAnnotationToolbarPresetStyles)
      ),
      createdAt: typeof o.createdAt === "string" ? o.createdAt : undefined,
      updatedAt: typeof o.updatedAt === "string" ? o.updatedAt : undefined,
    });
  }
  return out;
}

export const doGetPdfAnnotationToolbarPresets = (
  axios: AxiosInstance
): Promise<U3140PdfAnnotationToolbarPreset[]> => {
  return axios
    .get(u3140PresetEndpoints.GETPDFANNOTATIONTOOLBARPRESETS)
    .then((res) => u3140ParsePdfAnnotationToolbarPresets(u3140UnwrapResult(res.data)));
};

export const doCreatePdfAnnotationToolbarPreset = (
  axios: AxiosInstance,
  payload: U3140PdfAnnotationToolbarPresetCreatePayload
) => {
  return axios.post(u3140PresetEndpoints.CREATEPDFANNOTATIONTOOLBARPRESET, payload);
};

export const doUpdatePdfAnnotationToolbarPreset = (
  axios: AxiosInstance,
  presetId: string | number,
  payload: U3140PdfAnnotationToolbarPresetUpdatePayload
) => {
  return axios.put(u3140PresetEndpoints.UPDATEPDFANNOTATIONTOOLBARPRESET(presetId), payload);
};

export const doDeletePdfAnnotationToolbarPreset = (axios: AxiosInstance, presetId: string | number) => {
  return axios.delete(u3140PresetEndpoints.DELETEPDFANNOTATIONTOOLBARPRESET(presetId));
};

/** Stub pour `U2000HandleApiError` (handlers annotations PDF). */
/* eslint-disable @typescript-eslint/no-unused-vars -- signature imposée par U2000HandleApiError */
export function luHandleAppiError(
  t: TranslateFn,
  NS: string,
  err: unknown,
  errorResult: U2000ApiErrorResult
): void {
  void t;
  void NS;
  void err;
  void errorResult;
}
/* eslint-enable @typescript-eslint/no-unused-vars */
